import { useRef, useState } from "react";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { useToast } from "../../components/Toast";
import { ApiError, apiGet, serverBesked } from "../../lib/customFetch";
import { downloadFil } from "../../lib/download";
import styles from "./admin.module.css";

function iDagIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function maanedStartIso(): string {
  return `${iDagIso().slice(0, 8)}01`;
}

type Loenkategori = "arbejdstid" | "ferie" | "sygdom" | "fri";

interface LoenPreview {
  fra: string;
  til: string;
  format: "danloen";
  ready: boolean;
  mangler: Array<{ kode: string; besked: string }>;
  inkluderedeKategorier: Loenkategori[];
  udeladteKategorier: Loenkategori[];
  totaler: {
    linjer: number;
    medarbejdere: number;
    minutter: number;
    enheder: number;
  };
}

type LoenState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "failed"; besked: string }
  | { status: "ok"; preview: LoenPreview };

const KATEGORI_TEKST: Record<Loenkategori, string> = {
  arbejdstid: "Arbejdstid",
  ferie: "Ferie",
  sygdom: "Sygdom",
  fri: "Fri",
};

function parseLoenPreview(value: unknown): LoenPreview {
  if (value === null || typeof value !== "object") {
    throw new Error("Ugyldigt lønpreview");
  }
  const record = value as Record<string, unknown>;
  const totaler = record.totaler as Record<string, unknown> | undefined;
  const mangler = record.mangler;
  const inkluderet = record.inkluderedeKategorier;
  const udeladt = record.udeladteKategorier;
  const gyldigKategori = (kategori: unknown): kategori is Loenkategori =>
    typeof kategori === "string" && kategori in KATEGORI_TEKST;
  if (
    typeof record.fra !== "string" ||
    typeof record.til !== "string" ||
    record.format !== "danloen" ||
    typeof record.ready !== "boolean" ||
    !Array.isArray(mangler) ||
    !mangler.every(
      (mangel) =>
        mangel !== null &&
        typeof mangel === "object" &&
        typeof (mangel as Record<string, unknown>).kode === "string" &&
        typeof (mangel as Record<string, unknown>).besked === "string",
    ) ||
    !Array.isArray(inkluderet) ||
    !inkluderet.every(gyldigKategori) ||
    !Array.isArray(udeladt) ||
    !udeladt.every(gyldigKategori) ||
    totaler === undefined ||
    !["linjer", "medarbejdere", "minutter", "enheder"].every(
      (key) => typeof totaler[key] === "number",
    )
  ) {
    throw new Error("Ugyldigt lønpreview");
  }
  return value as LoenPreview;
}

/**
 * Eksport (fase 2c): CSV-rådata i helårs-bidder + PDF-ugeseddel.
 * Decision 0007: eksporten dækker ALTID fuld historik — gratis-grænsen
 * begrænser kun UI-visning, aldrig eksport.
 */
export function EksportView() {
  const toast = useToast();
  const [fra, setFra] = useState(maanedStartIso);
  const [til, setTil] = useState(iDagIso);
  const [uge, setUge] = useState(iDagIso);
  const [busy, setBusy] = useState<"csv" | "pdf" | "loen" | null>(null);
  const [loen, setLoen] = useState<LoenState>({ status: "idle" });
  const loenRequestId = useRef(0);

  const gyldigPeriode = (): boolean => {
    if (fra === "" || til === "" || fra > til) {
      toast.show("Vælg en gyldig periode (fra ≤ til).", "error");
      return false;
    }
    return true;
  };

  const setPeriodeFra = (value: string) => {
    loenRequestId.current += 1;
    setFra(value);
    setLoen({ status: "idle" });
  };

  const setPeriodeTil = (value: string) => {
    loenRequestId.current += 1;
    setTil(value);
    setLoen({ status: "idle" });
  };

  const kontrollerLoenfil = () => {
    if (!gyldigPeriode()) return;
    const requestId = loenRequestId.current + 1;
    loenRequestId.current = requestId;
    const requestedFra = fra;
    const requestedTil = til;
    setLoen({ status: "loading" });
    apiGet<unknown>(
      `/api/clockin/admin/export/payroll/preview?fra=${requestedFra}&til=${requestedTil}`,
    )
      .then((value) => {
        if (loenRequestId.current !== requestId) return;
        const preview = parseLoenPreview(value);
        if (preview.fra !== requestedFra || preview.til !== requestedTil) {
          throw new Error("Lønpreviewets periode matcher ikke forespørgslen.");
        }
        setLoen({ status: "ok", preview });
      })
      .catch((error: unknown) => {
        if (loenRequestId.current !== requestId) return;
        setLoen({
          status: "failed",
          besked:
            (error instanceof ApiError ? serverBesked(error) : null) ??
            "Kunne ikke kontrollere lønfilen — prøv igen.",
        });
      });
  };

  const hentLoenfil = () => {
    if (loen.status !== "ok" || !loen.preview.ready || !gyldigPeriode()) return;
    setBusy("loen");
    downloadFil(
      `/api/clockin/admin/export/payroll/danloen.csv?fra=${fra}&til=${til}`,
      `loen-danloen-${fra}-${til}.csv`,
    )
      .then(() => toast.show("Danløn-filen er hentet.", "success"))
      .catch((error: unknown) => {
        setLoen({
          status: "failed",
          besked:
            (error instanceof ApiError ? serverBesked(error) : null) ??
            "Lønfilen ændrede sig — kontrollér den igen.",
        });
      })
      .finally(() => setBusy(null));
  };

  const hentCsv = () => {
    if (!gyldigPeriode()) return;
    setBusy("csv");
    downloadFil(
      `/api/clockin/admin/export/csv?fra=${fra}&til=${til}`,
      `stemplinger-${fra}-${til}.csv`,
    )
      .then(() => toast.show("CSV hentet.", "success"))
      .catch((error: unknown) =>
        toast.show(
          (error instanceof ApiError ? serverBesked(error) : null) ??
            "Kunne ikke hente CSV — prøv igen.",
          "error",
        ),
      )
      .finally(() => setBusy(null));
  };

  const hentPdf = () => {
    if (uge === "") {
      toast.show("Vælg en dato i den ønskede uge.", "error");
      return;
    }
    setBusy("pdf");
    downloadFil(
      `/api/clockin/admin/export/ugeseddel.pdf?uge=${uge}`,
      `ugeseddel-${uge}.pdf`,
    )
      .then(() => toast.show("Ugeseddel hentet.", "success"))
      .catch((error: unknown) =>
        toast.show(
          (error instanceof ApiError ? serverBesked(error) : null) ??
            "Kunne ikke hente ugesedlen — prøv igen.",
          "error",
        ),
      )
      .finally(() => setBusy(null));
  };

  return (
    <div className={styles.eksport}>
      <section className={`${styles.eksportSektion} ${styles.loenEksport}`}>
        <div>
          <p className={styles.eksportEyebrow}>Lønklar fil</p>
          <h3 className={styles.eksportTitel}>Danløn-format</h3>
        </div>
        <p className={styles.eksportHint}>
          Kontrollér godkendte timer og fravær, før filen hentes. Salary kan
          også importere Danløn-formatet. Filen skal altid gennemgås i
          lønsystemets preview før lønnen godkendes.
        </p>
        <div className={styles.eksportFelter}>
          <Field
            label="Lønperiode fra"
            type="date"
            value={fra}
            disabled={loen.status === "loading"}
            onChange={(event) => setPeriodeFra(event.target.value)}
          />
          <Field
            label="Lønperiode til"
            type="date"
            value={til}
            disabled={loen.status === "loading"}
            onChange={(event) => setPeriodeTil(event.target.value)}
          />
        </div>
        <Button onClick={kontrollerLoenfil} disabled={loen.status === "loading" || busy !== null}>
          {loen.status === "loading" ? "Kontrollerer…" : "Kontrollér lønfil"}
        </Button>

        {loen.status === "failed" && (
          <div className={styles.loenProblem} role="alert">
            <p>{loen.besked}</p>
            <Button variant="secondary" onClick={kontrollerLoenfil}>
              Prøv igen
            </Button>
          </div>
        )}
        {loen.status === "ok" && (
          <div
            className={`${styles.loenPreview} ${loen.preview.ready ? styles.loenKlar ?? "" : styles.loenMangler ?? ""}`}
            aria-live="polite"
          >
            <div className={styles.loenPreviewHeader}>
              <div>
                <strong>{loen.preview.ready ? "Klar til download" : "Mangler før download"}</strong>
                <span>{loen.preview.fra} – {loen.preview.til}</span>
              </div>
              <span className={styles.loenStatus}>{loen.preview.ready ? "Klar" : `${String(loen.preview.mangler.length)} mangler`}</span>
            </div>
            <dl className={styles.loenTotaler}>
              <div><dt>Medarbejdere</dt><dd>{loen.preview.totaler.medarbejdere}</dd></div>
              <div><dt>Linjer</dt><dd>{loen.preview.totaler.linjer}</dd></div>
              <div><dt>Timer</dt><dd>{(loen.preview.totaler.minutter / 60).toLocaleString("da-DK", { maximumFractionDigits: 2 })}</dd></div>
            </dl>
            <p className={styles.eksportHint}>
              DanLøn-filen skriver timer som hele hundrededele uden
              decimalskilletegn: 119,88 timer bliver til 11988 enheder.
            </p>
            <div className={styles.loenKategorier}>
              <p><strong>Med:</strong> {loen.preview.inkluderedeKategorier.map((kategori) => KATEGORI_TEKST[kategori]).join(", ") || "Ingen"}</p>
              <p><strong>Udeladt:</strong> {loen.preview.udeladteKategorier.map((kategori) => KATEGORI_TEKST[kategori]).join(", ") || "Ingen"}</p>
            </div>
            {loen.preview.mangler.length > 0 && (
              <ul className={styles.loenMangelListe}>
                {loen.preview.mangler.map((mangel, index) => (
                  <li key={`${mangel.kode}-${String(index)}`}>{mangel.besked}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <Button
          onClick={hentLoenfil}
          disabled={loen.status !== "ok" || !loen.preview.ready || busy !== null}
        >
          {busy === "loen" ? "Henter…" : "Hent Danløn-fil"}
        </Button>
      </section>

      <section className={styles.eksportSektion}>
        <h3 className={styles.eksportTitel}>CSV — rådata</h3>
        <p className={styles.eksportHint}>
          Én række pr. stempling med dagens brutto, pause og netto. Højst 366
          dage pr. fil; hele historikken kan hentes i flere bidder.
        </p>
        <div className={styles.eksportFelter}>
          <Field
            label="Fra"
            type="date"
            value={fra}
            onChange={(e) => setPeriodeFra(e.target.value)}
          />
          <Field
            label="Til"
            type="date"
            value={til}
            onChange={(e) => setPeriodeTil(e.target.value)}
          />
        </div>
        <div>
          <Button onClick={hentCsv} disabled={busy !== null}>
            {busy === "csv" ? "Henter…" : "Hent CSV"}
          </Button>
        </div>
      </section>

      <section className={styles.eksportSektion}>
        <h3 className={styles.eksportTitel}>PDF-ugeseddel</h3>
        <p className={styles.eksportHint}>
          Print-klar ugeseddel pr. medarbejder med underskriftslinjer —
          vælg en vilkårlig dato, så tager vi ugen omkring den.
        </p>
        <div className={styles.eksportFelter}>
          <Field
            label="Dato i ugen"
            type="date"
            value={uge}
            onChange={(e) => setUge(e.target.value)}
          />
        </div>
        <div>
          <Button onClick={hentPdf} disabled={busy !== null}>
            {busy === "pdf" ? "Henter…" : "Hent ugeseddel"}
          </Button>
        </div>
      </section>
    </div>
  );
}
